// Operaciones · Torniquete · Diagnóstico
//
// "¿Por qué no le abrió a esta persona?" en una frase, con la evidencia debajo: lo que
// dice nuestra base (sus inscripciones y su mes pagado) contra lo que dice el equipo (su
// ficha y sus últimos intentos). Es la consulta que antes había que responder por consola.

import React, { useState } from 'react';
import {
  Card, Table, Tag, Space, Typography, Row, Col, Button, Modal, Alert,
  Input, InputNumber, Descriptions, Spin,
} from 'antd';
import {
  SearchOutlined, UnlockOutlined, StopOutlined, IdcardOutlined,
  ExclamationCircleOutlined, CheckCircleOutlined, CloseCircleOutlined,
} from '@ant-design/icons';
import { AccesoAdmin } from '../../../../service/Acceso';
import { mensajeDeError } from '../../../../service/client';
import { useNotify } from '../../../../providers/NotificationProvider';

const { Text, Paragraph } = Typography;

const COLOR_VEREDICTO = {
  staff: 'blue',
  habilitado: 'green',
  'staff-sin-ficha': 'orange',
  'sin-ficha': 'orange',
  'ventana-vencida': 'orange',
  'fuera-de-horario': 'gold',
  'sin-mes-pagado': 'red',
  'sin-inscripcion': 'red',
};

const ESTADO_VENTANA = {
  permanente: { color: 'blue', label: 'Permanente' },
  vigente: { color: 'green', label: 'Vigente ahora' },
  vencida: { color: 'default', label: 'Vencida' },
  'sin-ventana': { color: 'red', label: 'Sin ventana' },
};

export default function TorniqueteDiagnostico() {
  const notify = useNotify();

  const [rut, setRut] = useState('');
  const [persona, setPersona] = useState(null);
  const [buscando, setBuscando] = useState(false);
  const [error, setError] = useState(null);
  const [minutos, setMinutos] = useState(30);

  const buscar = async () => {
    if (!rut.trim()) return;
    setBuscando(true);
    setError(null);
    setPersona(null);
    try {
      setPersona(await AccesoAdmin.persona(rut.trim()));
    } catch (e) {
      setError(mensajeDeError(e));
    } finally {
      setBuscando(false);
    }
  };

  const habilitar = () => Modal.confirm({
    title: 'Habilitar el acceso ahora',
    icon: <ExclamationCircleOutlined />,
    content: (
      <span>
        Se le abre el torniquete a <b>{persona?.ficha?.nombre || persona?.rut}</b> por{' '}
        <b>{minutos} minutos</b>. Es una excepción de mostrador: no reemplaza su inscripción.
      </span>
    ),
    okText: 'Habilitar',
    cancelText: 'Cancelar',
    onOk: async () => {
      try {
        const r = await AccesoAdmin.habilitar({ rut: persona.rut, minutos });
        notify?.success?.(
          r.empujadoAlEquipo
            ? `Habilitado hasta ${r.hasta}. Ya puede pasar.`
            : `Habilitado hasta ${r.hasta}, pero el equipo todavía no lo recibió: puede tardar unos minutos.`
        );
        buscar();
      } catch (e) {
        notify?.error?.(mensajeDeError(e));
      }
    },
  });

  const darDeBaja = () => Modal.confirm({
    title: 'Dar de baja el acceso',
    icon: <ExclamationCircleOutlined />,
    okButtonProps: { danger: true },
    content: (
      <span>
        Se le vence la ventana a <b>{persona?.ficha?.nombre || persona?.rut}</b>. La ficha no
        se borra y el sistema se la vuelve a abrir antes de su próxima clase si tiene el mes
        al día.
      </span>
    ),
    okText: 'Dar de baja',
    cancelText: 'Cancelar',
    onOk: async () => {
      try {
        await AccesoAdmin.baja({ rut: persona.rut });
        notify?.success?.('Acceso dado de baja');
        buscar();
      } catch (e) {
        notify?.error?.(mensajeDeError(e));
      }
    },
  });

  return (
    <Card size="small" title={<span><IdcardOutlined /> ¿Por qué no le abrió?</span>}>
      <Space.Compact style={{ width: '100%', maxWidth: 460 }}>
        <Input
          placeholder="RUT del participante (ej. 16932655-K)"
          value={rut}
          onChange={(e) => setRut(e.target.value)}
          onPressEnter={buscar}
          allowClear
        />
        <Button type="primary" icon={<SearchOutlined />} loading={buscando} onClick={buscar}>
          Buscar
        </Button>
      </Space.Compact>

      <Paragraph type="secondary" style={{ fontSize: 12, marginTop: 8, marginBottom: 0 }}>
        El RUT es el del <b>participante</b>, no el del apoderado que pagó. Si el apoderado
        entra con su propia cédula, búscalo por el suyo.
      </Paragraph>

      {error && <Alert type="error" showIcon style={{ marginTop: 12 }} message={error} />}
      {buscando && <div style={{ padding: 24, textAlign: 'center' }}><Spin /></div>}

      {persona && (
        <div style={{ marginTop: 16 }}>
          <Alert
            type={persona.veredicto?.puedeEntrar ? 'success' : 'warning'}
            showIcon
            icon={persona.veredicto?.puedeEntrar ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
            message={
              <Space wrap>
                <b>{persona.veredicto?.puedeEntrar ? 'Puede entrar' : 'No puede entrar'}</b>
                <Tag color={COLOR_VEREDICTO[persona.veredicto?.codigo] || 'default'}>
                  {persona.veredicto?.codigo}
                </Tag>
                {persona.esStaff && <Tag color="blue">Personal</Tag>}
              </Space>
            }
            description={persona.veredicto?.mensaje}
          />

          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col xs={24} lg={12}>
              <Descriptions size="small" column={1} bordered title="Ficha en el equipo">
                {persona.ficha ? (
                  <>
                    <Descriptions.Item label="Nombre">{persona.ficha.nombre || '—'}</Descriptions.Item>
                    <Descriptions.Item label="Ventana">
                      <Space direction="vertical" size={0}>
                        <Text>{persona.ficha.desde} → {persona.ficha.hasta}</Text>
                        <Tag color={ESTADO_VENTANA[persona.ficha.estadoVentana]?.color}>
                          {ESTADO_VENTANA[persona.ficha.estadoVentana]?.label || persona.ficha.estadoVentana}
                        </Tag>
                      </Space>
                    </Descriptions.Item>
                    <Descriptions.Item label="Tarjeta / documento">
                      {persona.ficha.tarjeta || '—'} / {persona.ficha.documento || '—'}
                      {(!persona.ficha.tarjeta || !persona.ficha.documento) && (
                        <Tag color="red" style={{ marginLeft: 8 }}>El lector no la reconoce sin esto</Tag>
                      )}
                    </Descriptions.Item>
                    <Descriptions.Item label="PIN (para teclear)">{persona.pin}</Descriptions.Item>
                  </>
                ) : (
                  <Descriptions.Item label="Ficha">
                    <Text type="secondary">No está registrada en el equipo</Text>
                  </Descriptions.Item>
                )}
              </Descriptions>

              <Space style={{ marginTop: 12 }} wrap>
                <InputNumber min={1} max={120} value={minutos} onChange={setMinutos} addonAfter="min" />
                <Button icon={<UnlockOutlined />} onClick={habilitar}>Habilitar ahora</Button>
                <Button danger icon={<StopOutlined />} disabled={!persona.ficha} onClick={darDeBaja}>
                  Dar de baja
                </Button>
              </Space>
            </Col>

            <Col xs={24} lg={12}>
              <Table
                size="small"
                rowKey={(r) => r.inscripcionId}
                title={() => 'Inscripciones activas'}
                dataSource={persona.inscripciones}
                pagination={false}
                locale={{ emptyText: 'Sin inscripciones activas' }}
                columns={[
                  { title: 'Taller', dataIndex: 'taller', ellipsis: true },
                  {
                    title: 'Mes pagado',
                    render: (_, r) => (
                      <Space direction="vertical" size={0}>
                        <Text style={{ fontSize: 12 }}>{r.mesPagado?.desde} → {r.mesPagado?.hasta}</Text>
                        <Tag color={r.alDia ? 'green' : 'red'}>{r.alDia ? 'al día' : 'vencido'}</Tag>
                      </Space>
                    ),
                  },
                  {
                    title: 'Bloque',
                    render: (_, r) => (
                      <Space direction="vertical" size={0}>
                        <Text style={{ fontSize: 12 }}>{r.bloqueHoy || 'no tiene hoy'}</Text>
                        {r.bloqueEnCurso && <Tag color="green">en curso</Tag>}
                      </Space>
                    ),
                  },
                ]}
              />

              <Table
                size="small"
                style={{ marginTop: 12 }}
                rowKey={(r, i) => `${r.fecha}-${i}`}
                title={() => 'Sus últimos intentos en el torniquete'}
                dataSource={persona.eventos}
                pagination={false}
                scroll={{ y: 180 }}
                locale={{ emptyText: 'Nunca pasó por el torniquete' }}
                columns={[
                  { title: 'Fecha', dataIndex: 'fecha', width: 160 },
                  { title: 'Evento', dataIndex: 'evento' },
                ]}
              />
            </Col>
          </Row>
        </div>
      )}
    </Card>
  );
}
